import React from 'react'
import { Trans } from '@lingui/react/macro'
import { useLingui } from '@lingui/react/macro'
import { XStack, YStack } from 'tamagui'

import { Card } from '@newComponents/Card'
import { Icon } from '@newComponents/Icon'
import { Paragraph } from '@newComponents/Text'
import { Link } from '@newComponents/Link'

import { DocSection } from '../shared/DocSection'
import { DocScreenshot } from '../shared/DocScreenshot'

const Rsvps = () => {
  const { t } = useLingui()

  return (
    <>
      <Card gap='$4'>
        <Paragraph size='$5'>
          <Trans>
            When someone RSVPs to your Event, they choose how they plan to
            attend:{' '}
            <Paragraph size='$5' fontWeight='$7'>
              attending in-person
            </Paragraph>{' '}
            or{' '}
            <Paragraph size='$5' fontWeight='$7'>
              attending virtual
            </Paragraph>
            . This helps organizers plan capacity for both the physical venue
            and the online stream.
          </Trans>
        </Paragraph>
        <DocScreenshot
          src='docs/events/event-page.png'
          alt={t`Event page showing attendee profile photo avatars with count and the Attend button for RSVPing`}
          caption={t`Attendee avatars and the Attend button on the Event page`}
        />
        <Paragraph size='$5'>
          <Trans>
            On the <Link href='/docs/events/page'>Event page</Link>, attendees
            click the{' '}
            <Paragraph size='$5' fontWeight='$7'>
              Attend
            </Paragraph>{' '}
            button to RSVP. For hybrid Events, they select their preferred
            attendance mode. Their profile photo appears in the attendee list
            immediately, showing other visitors who is going.
          </Trans>
        </Paragraph>
      </Card>

      <Card gap='$4'>
        <DocSection id='rsvp-features' title={t`RSVP Features`}>
          <YStack gap='$3'>
            <XStack gap='$3' alignItems='flex-start'>
              <Icon
                name='people'
                size='$5'
                color='$color'
                flexShrink={0}
                marginTop={2}
              />
              <Paragraph size='$5'>
                <Trans>
                  <Paragraph size='$5' fontWeight='$7'>
                    Capacity and waitlists
                  </Paragraph>{' '}
                  — You can set an attendee limit for in-person attendance in{' '}
                  <Link href='/docs/events/settings'>Event Settings</Link>. When
                  the limit is reached, additional in-person RSVPs are
                  automatically placed on a waitlist. If a spot opens up — for
                  example, when someone cancels — the first person on the
                  waitlist is automatically promoted to attending. Online
                  attendance has no built-in capacity limit.
                </Trans>
              </Paragraph>
            </XStack>
            <XStack gap='$3' alignItems='flex-start'>
              <Icon
                name='edit'
                size='$5'
                color='$color'
                flexShrink={0}
                marginTop={2}
              />
              <Paragraph size='$5'>
                <Trans>
                  <Paragraph size='$5' fontWeight='$7'>
                    Attendee information
                  </Paragraph>{' '}
                  — Organizers can add optional{' '}
                  <Link href='/docs/events/attendee-forms'>attendee forms</Link>{' '}
                  to collect additional information during registration, such as
                  dietary requirements, t-shirt sizes, or company names.
                </Trans>
              </Paragraph>
            </XStack>
            <XStack gap='$3' alignItems='flex-start'>
              <Icon
                name='person'
                size='$5'
                color='$color'
                flexShrink={0}
                marginTop={2}
              />
              <Paragraph size='$5'>
                <Trans>
                  <Paragraph size='$5' fontWeight='$7'>
                    Canceling attendance
                  </Paragraph>{' '}
                  — Attendees can cancel their RSVP at any time from the Event
                  page. If the Event has a waitlist, canceling frees up a spot
                  for the next person in line.
                </Trans>
              </Paragraph>
            </XStack>
            <XStack gap='$3' alignItems='flex-start'>
              <Icon
                name='link'
                size='$5'
                color='$color'
                flexShrink={0}
                marginTop={2}
              />
              <Paragraph size='$5'>
                <Trans>
                  <Paragraph size='$5' fontWeight='$7'>
                    Social profile links
                  </Paragraph>{' '}
                  — Attendee rows on the Event page display each person&apos;s
                  social profile links (such as Bluesky, Twitter/X, GitHub, and
                  LinkedIn) alongside their name. This makes it easy for
                  attendees to connect with each other before or after the
                  Event. Members can add their social links from their{' '}
                  <Link href='/docs/account/profile'>profile settings</Link>.
                </Trans>
              </Paragraph>
            </XStack>
          </YStack>
        </DocSection>
      </Card>

      <Card gap='$4'>
        <DocSection id='paid-events' title={t`Paid Events`}>
          <Paragraph size='$5'>
            <Trans>
              For paid Events, attendees purchase tickets instead of RSVPing for
              free. See{' '}
              <Link href='/docs/events/ticketing-overview'>
                Ticketing & Payments
              </Link>{' '}
              for details on setting up ticket tiers, checkout, and paid ticket
              order records.
            </Trans>
          </Paragraph>
        </DocSection>
      </Card>
    </>
  )
}

export default Rsvps
